import styled from "@emotion/styled"
import { Value } from "@stringtale/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import MostViewedLessonCard from "app/home/components/MostViewedLessonCard"
import type { MostViewedLessonsDays } from "app/home/queries/getMostViewedLessons"
import { trpc } from "app/trpc"
import { Suspense, useState } from "react"
import { PX14, PX24 } from "theme/sizes"
import Container from "ui/Container"
import Group from "ui/Group"
import Loader from "ui/Loader"
import Stack from "ui/Stack"
import Tab from "ui/Tab"
import Text from "ui/Text"
import Title from "ui/Title"

const DAY_OPTIONS: { value: MostViewedLessonsDays; label: string }[] = [
  { value: 1, label: "1d" },
  { value: 3, label: "3d" },
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
]

const getTitleFormat = (days: MostViewedLessonsDays) => ({
  days,
  periodLabel: days === 1 ? "dag" : "dagen",
})

const Section = styled(Container.Root)`
  margin: 0 0 60px;
`

const HeaderRow = styled(Group)`
  width: 100%;
  align-items: flex-start;
`

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;

  @media screen and (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const Column = styled(Stack)`
  min-width: 0;
`

const LoadingFallback = styled(Stack)`
  width: 100%;
  min-height: 120px;
`

const MostViewedLessonsGrid = ({ days }: { days: MostViewedLessonsDays }) => {
  const { data: mostViewedLessons } = useSuspenseQuery(
    trpc.home.getMostViewedLessons.queryOptions({ days })
  )

  if (mostViewedLessons.length === 0) {
    return null
  }

  const columns = [
    mostViewedLessons.slice(0, 3),
    mostViewedLessons.slice(3, 6),
    mostViewedLessons.slice(6, 9),
  ].filter((column) => column.length > 0)

  return (
    <Grid>
      {columns.map((column, columnIndex) => (
        <Column key={columnIndex} gap="16px">
          {column.map((item) => (
            <MostViewedLessonCard key={item.lesson.id} item={item} />
          ))}
        </Column>
      ))}
    </Grid>
  )
}

const MostViewedLessons = () => {
  const [days, setDays] = useState<MostViewedLessonsDays>(7)

  return (
    <Section>
      <Container.Content>
        <Stack align="flex-start" gap="20px">
          <HeaderRow justify="space-between" gap="16px">
            <Stack align="flex-start" gap="8px">
              <Title size={PX24} weight="900">
                <Value
                  name="apps.web.src.home.components.mostviewedlessons.meest_bekeken_lessen_afgelopen"
                  format={getTitleFormat(days)}
                >
                  {`Meest bekeken lessen afgelopen {days} {periodLabel}`}
                </Value>
              </Title>
              <Text size={PX14}>
                <Value name="apps.web.src.home.components.mostviewedlessons.technische_test_admin_only">
                  Dit is een technische test. Dit onderdeel is enkel zichtbaar
                  voor admins.
                </Value>
              </Text>
            </Stack>
            <Group gap="8px">
              {DAY_OPTIONS.map((option) => (
                <Tab
                  key={option.value}
                  size="small"
                  isActive={days === option.value}
                  onClick={() => setDays(option.value)}
                >
                  {option.label}
                </Tab>
              ))}
            </Group>
          </HeaderRow>
          <Suspense
            fallback={
              <LoadingFallback align="center" justify="center">
                <Loader size="32px" />
              </LoadingFallback>
            }
          >
            <MostViewedLessonsGrid key={days} days={days} />
          </Suspense>
        </Stack>
      </Container.Content>
    </Section>
  )
}

export default MostViewedLessons
